import ProjectsGrid from './ProjectsGrid'
import ProjectsList from './ProjectsList'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Trans } from 'next-i18next'
import Link, { LinkProps } from '@mui/material/Link'
import { useRouter } from 'next/router'
import NextLink from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Project } from '@/interfaces/projects'
import { ViewModeType } from '@/interfaces/filters'
import { getDashboardUrl } from '@/helpers/uri'

interface ProjectsViewProps {
  projects: Project[]
  viewMode: ViewModeType
  gridColumns?: number
  showCreateMessage?: boolean
}

const CustomLink = ({ children, ...props }: LinkProps): React.ReactElement => {
  return (
    <NextLink href={props.href} passHref legacyBehavior prefetch={false}>
      <Link {...props}>{children}</Link>
    </NextLink>
  )
}

const ProjectsNotFound = ({ showCreateMessage }: { showCreateMessage?: boolean }): React.ReactElement => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const urlWithoutQuery = router.asPath.split('?')[0]
  const inSearchMode = searchParams.get('search')

  if (!showCreateMessage) {
    return (
      <Box textAlign="center" paddingX={5} paddingY={10}>
        <Typography variant="h6" component="h3">
          <Trans i18nKey={inSearchMode ? 'projects:noItemsFound' : 'projects:noCreatedProjects'} />
        </Typography>
      </Box>
    )
  }

  return (
    <Box textAlign="center" paddingX={5} paddingY={10}>
      <Typography variant="h6" component="h3">
        <Trans i18nKey="projects:projectsNotFoundAndCreate.0">
          No results found! Be the first to <Link href={getDashboardUrl('?new')}>create a project</Link> in this space.
        </Trans>
      </Typography>
      <Typography component="p">
        <Trans i18nKey="projects:projectsNotFoundAndCreate.1">
          or
          <CustomLink href={urlWithoutQuery}>clear the search filters</CustomLink>
          to return more results
        </Trans>
      </Typography>
    </Box>
  )
}

const ProjectsView = ({
  projects,
  viewMode,
  gridColumns,
  showCreateMessage
}: ProjectsViewProps): React.ReactElement => {
  if (projects.length === 0) {
    return <ProjectsNotFound showCreateMessage={showCreateMessage} />
  }
  if (viewMode === 'listView') {
    return <ProjectsList projects={projects} />
  } else {
    return <ProjectsGrid projects={projects} columns={gridColumns} />
  }
}

export default ProjectsView
